import React from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import AnalogyCard from '../components/AnalogyCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertTriangle, TrendingDown, DollarSign, Target, HelpCircle } from 'lucide-react';
import { cspAnalogies } from '../mock';
import SEO, { generateArticleSchema, generateBreadcrumbSchema, generateFAQSchema } from '../components/SEO';
import AdSense from '../components/AdSense';

const CSP = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cash-Secured Put Explained for Beginners (Analogies, Examples & Risks)"
        description="Learn cash-secured puts in plain English. Real-world analogies, worked examples with numbers, risks explained. Options for beginners."
        keywords="cash secured put explained, cash secured put for beginners, cash secured put analogy, cash secured put example, what is a cash secured put, CSP options explained simply"
        canonical="https://optionsanalogies.com/csp"
        type="article"
        article={true}
        publishedTime="2025-01-15T00:00:00Z"
        modifiedTime="2025-01-15T00:00:00Z"
        schema={[
          generateArticleSchema({
            title: "Cash-Secured Put Explained for Beginners (Analogies, Examples & Risks)",
            description: "Learn cash-secured puts in plain English with real-world analogies and worked examples.",
            url: "https://optionsanalogies.com/csp",
            datePublished: "2025-01-15T00:00:00Z",
            dateModified: "2025-01-15T00:00:00Z",
            image: "https://optionsanalogies.com/og-csp.png"
          }),
          generateBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Cash-Secured Puts", path: "/csp" }
          ]),
          generateFAQSchema([
            {
              question: "What is a cash-secured put in simple terms?",
              answer: "A cash-secured put is like getting paid NOW to promise you'll buy a stock LATER at a specific price (the strike price). You keep the money (premium) upfront, and you set aside enough cash to buy the stock if the seller exercises their right."
            },
            {
              question: "Can I lose money selling cash-secured puts?",
              answer: "Yes. If the stock price falls significantly below your strike price, you must buy the stock at the higher strike price, resulting in a paper loss. The premium you collected reduces but doesn't eliminate this loss. Cash-secured puts carry substantial risk."
            },
            {
              question: "Is a cash-secured put the same as a limit order?",
              answer: "They're similar but different. Both let you buy at a target price, but a CSP pays you premium upfront to wait. A limit order costs nothing but earns nothing. CSPs obligate you to buy if assigned; limit orders don't."
            },
            {
              question: "How much cash do I need for a cash-secured put?",
              answer: "You need enough cash to buy 100 shares at the strike price. For example, a $50 strike requires $5,000 in cash as collateral. This cash is held by your broker until the option expires or you're assigned."
            },
            {
              question: "What happens if I get assigned on a cash-secured put?",
              answer: "Assignment means you must buy 100 shares at the strike price using your reserved cash. This happens when the stock price falls below the strike at expiration. You keep the premium you collected, which reduces your effective purchase price."
            }
          ])
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'Cash-Secured Put', path: '/csp' }]} />

        {/* Header - Ad-Free Zone */}
        <div className="mb-12" data-nosnippet>
          <div className="mb-4">
            <span className="text-sm text-gray-400">Also known as:</span>
            <p className="text-gray-300">short put, sell-to-open put, secured put, cash-covered put</p>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Cash-Secured Put (CSP)
          </h1>
          <p className="text-2xl text-emerald-500 font-medium">
            "A paid limit order: you're paid now to promise you'll buy later at your price."
          </p>
        </div>

        {/* Core Mechanics - Ad-Free Zone */}
        <section className="mb-16" data-nosnippet>
          <h2 className="text-3xl font-bold text-white mb-8">Core Mechanics</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <DollarSign className="w-8 h-8 text-emerald-500 mb-2" />
                <CardTitle className="text-white">You Sell a Put</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  You collect premium upfront by selling a put option contract (100 shares per contract).
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <Target className="w-8 h-8 text-emerald-500 mb-2" />
                <CardTitle className="text-white">You Reserve Cash</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  Enough to buy 100 shares × strike price if assigned. This cash is held as collateral.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <TrendingDown className="w-8 h-8 text-red-500 mb-2" />
                <CardTitle className="text-white">If Stock Below Strike at Expiration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  "Claim" happens → you buy shares at strike price (assignment).
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <TrendingDown className="w-8 h-8 text-emerald-500 mb-2" />
                <CardTitle className="text-white">If Stock At/Above Strike</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  Option expires worthless; you keep the premium without obligation.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Math Tile */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Math (Per Share)</h2>
          <div className="glass rounded-xl p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-emerald-400 font-semibold mb-2">Max Profit</h3>
                <p className="text-2xl text-white font-mono">= Premium</p>
                <p className="text-sm text-gray-400 mt-2">Keep premium if stock stays above strike</p>
              </div>
              <div>
                <h3 className="text-emerald-400 font-semibold mb-2">Breakeven</h3>
                <p className="text-2xl text-white font-mono">= Strike − Premium</p>
                <p className="text-sm text-gray-400 mt-2">Point where you neither profit nor lose</p>
              </div>
              <div>
                <h3 className="text-red-400 font-semibold mb-2">Max Loss (Theoretical)</h3>
                <p className="text-2xl text-white font-mono">= Strike − Premium</p>
                <p className="text-sm text-gray-400 mt-2">If stock goes to $0 (unlikely but possible)</p>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-700">
              <h3 className="text-emerald-400 font-semibold mb-2">Capital Required</h3>
              <p className="text-2xl text-white font-mono">= Strike × 100</p>
              <p className="text-sm text-gray-400 mt-2">Per contract (100 shares)</p>
            </div>
          </div>
        </section>

        {/* Risk Callouts */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Risk Callouts</h2>
          <div className="space-y-4">
            <Alert className="border-red-500/50 bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <AlertDescription className="text-gray-300 ml-2">
                <strong className="text-white">Falling Stock Risk:</strong> A falling stock can assign you at a price higher than market value. You're obligated to buy at the strike even if the current price is much lower.
              </AlertDescription>
            </Alert>
            <Alert className="border-red-500/50 bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <AlertDescription className="text-gray-300 ml-2">
                <strong className="text-white">Limited Protection:</strong> Big drops hurt; premium only softens the blow. If the stock drops $10 and you collected $1 premium, you're still down $9 per share.
              </AlertDescription>
            </Alert>
          </div>
        </section>

        {/* Ad Placement 1 - After Math */}
        <section className="mb-16">
          <AdSense slot="5453478557" format="horizontal" />
        </section>

        {/* CSP Analogies */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">CSP Analogies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {cspAnalogies.map((analogy) => (
              <AnalogyCard key={analogy.id} analogy={analogy} />
            ))}
          </div>
        </section>

        {/* Ad Placement 2 - After Analogies */}
        <section className="mb-16">
          <AdSense slot="4052766972" format="rectangle" />
        </section>

        {/* When to Use */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">When to Use CSP</h2>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-300">
                    <strong className="text-white">Willing buyer at a discount:</strong> You genuinely want to own the stock at the strike price and prefer income while waiting for your entry.
                  </p>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-300">
                    <strong className="text-white">Neutral to mildly bullish:</strong> You believe the stock will stay above the strike or are comfortable owning shares if assigned.
                  </p>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-300">
                    <strong className="text-white">Disciplined entry strategy:</strong> You prefer systematic, patient capital deployment over emotional buying.
                  </p>
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* See Also - Internal Linking */}
        <section className="mb-16">
          <div className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Related Strategies</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-gray-900/50 border-gray-800 hover:border-emerald-500 transition">
                <CardHeader>
                  <CardTitle className="text-emerald-400">Covered Calls</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 mb-4">
                    Once you own shares (from CSP assignment), learn how <Link to="/covered-calls" className="text-emerald-400 hover:text-emerald-300 underline">covered calls</Link> let you generate income by selling calls against your position.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gray-900/50 border-gray-800 hover:border-emerald-500 transition">
                <CardHeader>
                  <CardTitle className="text-emerald-400">The Wheel Strategy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 mb-4">
                    See how <Link to="/wheel" className="text-emerald-400 hover:text-emerald-300 underline">the Wheel strategy</Link> combines cash-secured puts and covered calls into a repeatable income-generating cycle.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Beginner FAQs */}
        <section className="mb-16">
          <div className="glass rounded-xl p-8">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-8 h-8 text-emerald-500" />
              <h2 className="text-3xl font-bold text-white">Frequently Asked Questions</h2>
            </div>
            
            <div className="space-y-6">
              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">What is a cash-secured put in simple terms?</h3>
                <p className="text-gray-300 leading-relaxed">
                  A cash-secured put is like getting paid NOW to promise you'll buy a stock LATER at a specific price (the strike price). You keep the money (premium) upfront, and you set aside enough cash to buy the stock if the seller exercises their right.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Can I lose money selling cash-secured puts?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Yes. If the stock price falls significantly below your strike price, you must buy the stock at the higher strike price, resulting in a paper loss. The premium you collected reduces but doesn't eliminate this loss. Cash-secured puts carry substantial risk.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Is a cash-secured put the same as a limit order?</h3>
                <p className="text-gray-300 leading-relaxed">
                  They're similar but different. Both let you buy at a target price, but a CSP pays you premium upfront to wait. A limit order costs nothing but earns nothing. CSPs obligate you to buy if assigned; limit orders don't.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">How much cash do I need for a cash-secured put?</h3>
                <p className="text-gray-300 leading-relaxed">
                  You need enough cash to buy 100 shares at the strike price. For example, a $50 strike requires $5,000 in cash as collateral. This cash is held by your broker until the option expires or you're assigned.
                </p>
              </div>

              <div className="pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">What happens if I get assigned on a cash-secured put?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Assignment means you must buy 100 shares at the strike price using your reserved cash. This happens when the stock price falls below the strike at expiration. You keep the premium you collected, which reduces your effective purchase price.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Disclaimer */}
        <section className="mb-16">
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <AlertDescription className="text-gray-300 ml-2">
              <strong className="text-white">Education Only - Not Financial Advice:</strong> This content is for educational purposes only and does not constitute financial, tax, or legal advice. Options trading involves significant risk and may not be suitable for all investors. You could lose your entire investment. Before trading options, carefully consider your financial situation, investment objectives, and risk tolerance. Consult with a qualified financial advisor before making any investment decisions.
            </AlertDescription>
          </Alert>
        </section>
      </div>
    </div>
  );
};

export default CSP;